import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import * as Icons from "lucide-react";
import { Gem, X, Gift, Dice5, Briefcase, MapPin, PlayCircle, Sparkles, Car, Footprints, LayoutGrid, Navigation, Volume2, VolumeX, Megaphone } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, naira } from "@/lib/api";
import NaijaAvatar from "@/components/NaijaAvatar";
import { seededAppearance } from "@/lib/appearance";
import { Confetti, FloatUp } from "@/components/Fx";
import { sfx } from "@/lib/sfx";

const VENUE_ART = {
  buka: "https://static.prod-images.emergentagent.com/jobs/0305d694-6d38-4e56-9f10-ef46437aa1b3/images/a2c23c1798c91503a8e5eecd97e3e47872605cefa15f6934cde46003b228d747.jpeg",
  club: "https://static.prod-images.emergentagent.com/jobs/0305d694-6d38-4e56-9f10-ef46437aa1b3/images/92ef82eeef564726aa09795f40be203a4d62be5f02ef7603ba59e3beece6c5b3.jpeg",
  gym: "https://static.prod-images.emergentagent.com/jobs/0305d694-6d38-4e56-9f10-ef46437aa1b3/images/4869a1fbe8fb3aca62cc9aed99880602cb27c9de418fb3e0958c9afded4fe4a4.jpeg",
  bank: "https://static.prod-images.emergentagent.com/jobs/0305d694-6d38-4e56-9f10-ef46437aa1b3/images/e4d6960f694709df5a216988310644871e8cc32d3e4a7dfe9d0cadeb006e73e8.jpeg",
  hustle: "https://static.prod-images.emergentagent.com/jobs/0305d694-6d38-4e56-9f10-ef46437aa1b3/images/e70b32786858edbb426765f53204e5941f05ebb2ecfaef6c4248fec11656a472.jpeg",
};

const MAP = "https://static.prod-images.emergentagent.com/jobs/0305d694-6d38-4e56-9f10-ef46437aa1b3/images/891073e0e568129c7dacacb27b5a1c60d09ebfae7b68e657d6b36e2f685913b4.jpeg";
const WORLD_W = 1600;
const WORLD_H = 1073;
const WALK_SPEED = 165;   // px / sec
const DRIVE_SPEED = 360;
const ENTER_RADIUS = 95;

// Venues placed in world coords (percent of world).
const VENUES = [
  { id: "crib", name: "Your Crib", emoji: "🏠", x: 13, y: 22, color: "#00E5FF", nav: null },
  { id: "buka", name: "Mama Put", emoji: "🍲", x: 72, y: 16, color: "#FFD700", nav: null },
  { id: "bank", name: "Bank / POS", emoji: "🏦", x: 50, y: 30, color: "#16A34A", nav: null },
  { id: "hustle", name: "Hustle Hub", emoji: "💼", x: 20, y: 52, color: "#16A34A", nav: null },
  { id: "gym", name: "Gym", emoji: "🏋🏾", x: 52, y: 55, color: "#FF66B2", nav: null },
  { id: "market", name: "Market", emoji: "🛍️", x: 82, y: 48, color: "#FFD700", nav: "/shop" },
  { id: "club", name: "The Club", emoji: "🎉", x: 24, y: 80, color: "#FF0055", nav: null },
  { id: "block", name: "The Block", emoji: "🧍🏾", x: 83, y: 80, color: "#A78BFA", nav: "/social" },
  { id: "viewing", name: "Viewing Centre", emoji: "📺", x: 63, y: 66, color: "#00E5FF", nav: "/viewing-centre" },
  { id: "biz", name: "My Businesses", emoji: "🏪", x: 40, y: 72, color: "#FFD700", nav: "/businesses" },
];

const MODES = [
  { label: "Free Roam", emoji: "🗺️", desc: "Waka the streets", nav: null, color: "#16A34A" },
  { label: "Hustle", emoji: "💼", desc: "Work gigs for cash", nav: "/jobs", color: "#16A34A" },
  { label: "Viewing Centre", emoji: "⚽", desc: "Bet on matches", nav: "/viewing-centre", color: "#00E5FF" },
  { label: "Businesses", emoji: "🏪", desc: "Passive income", nav: "/businesses", color: "#FFD700" },
  { label: "Invest", emoji: "📈", desc: "Stocks & land", nav: "/invest", color: "#FF66B2" },
  { label: "Shop", emoji: "🛍️", desc: "Drip & cars", nav: "/shop", color: "#FFD700" },
  { label: "Phone", emoji: "📱", desc: "Social & news", nav: "/social", color: "#A78BFA" },
  { label: "Ranks", emoji: "🏆", desc: "Forbes list", nav: "/leaderboard", color: "#FFD700" },
];

function dayTint() {
  const h = new Date().getHours();
  if (h >= 5 && h < 7) return { label: "Dawn", icon: "🌅", bg: "linear-gradient(180deg, rgba(255,170,110,0.28), rgba(255,120,90,0.12))", op: 1, night: false };
  if (h >= 7 && h < 17) return { label: "Day", icon: "☀️", bg: "transparent", op: 0, night: false };
  if (h >= 17 && h < 19) return { label: "Dusk", icon: "🌇", bg: "linear-gradient(180deg, rgba(255,130,60,0.3), rgba(120,60,140,0.24))", op: 1, night: true };
  return { label: "Night", icon: "🌙", bg: "linear-gradient(180deg, rgba(15,18,70,0.52), rgba(8,8,36,0.62))", op: 1, night: true };
}

const NEED_META = {
  hunger: "#FFD700", energy: "#16A34A", fun: "#FF0055",
  social: "#FF66B2", hygiene: "#00E5FF", bladder: "#A78BFA",
};

function RoamChar({ appearance, dir, moving, driving }) {
  const flip = dir === "left";
  return (
    <div className="relative" style={{ width: 48, height: 72 }}>
      <div className="absolute left-1/2 -translate-x-1/2 rounded-[50%] bg-black/40 blur-[2px]"
        style={{ width: 30, height: 9, bottom: driving ? 2 : 0 }} />
      {driving ? (
        <motion.div animate={moving ? { y: [0, -1.5, 0] } : { y: 0 }} transition={{ duration: 0.25, repeat: moving ? Infinity : 0 }}
          className="absolute left-1/2 -translate-x-1/2 bottom-1 rounded-[10px] border-2 border-black/40 flex items-center justify-center"
          style={{ width: 40, height: 46, background: "linear-gradient(160deg,#FF0055,#A01038)", transform: `translateX(-50%) scaleX(${flip ? -1 : 1})` }}>
          <div className="w-7 h-3 rounded-sm bg-[#00E5FF]/70 absolute top-1.5" />
          <Car size={16} className="text-white/90" />
        </motion.div>
      ) : (
        <motion.div animate={moving ? { y: [0, -1.5, 0] } : { y: 0 }} transition={{ duration: 0.3, repeat: moving ? Infinity : 0 }}
          className="absolute left-1/2 -translate-x-1/2 bottom-1">
          <NaijaAvatar appearance={appearance} view="full" size={42} walking={moving} flip={flip} />
        </motion.div>
      )}
    </div>
  );
}

// Ambient crowd so the streets feel alive — deterministic varied looks.
const NPCS = [
  { id: "np1", x: 58, y: 23, rng: 20, dur: 11 },
  { id: "np2", x: 44, y: 38, rng: 26, dur: 14 },
  { id: "np3", x: 70, y: 52, rng: 18, dur: 9 },
  { id: "np4", x: 30, y: 60, rng: 24, dur: 13 },
  { id: "np5", x: 78, y: 70, rng: 16, dur: 10 },
  { id: "np6", x: 50, y: 74, rng: 22, dur: 12 },
  { id: "np7", x: 18, y: 42, rng: 20, dur: 15 },
  { id: "np8", x: 64, y: 84, rng: 18, dur: 11 },
].map((n) => ({ ...n, look: seededAppearance(n.id) }));

export default function City() {
  const { character, setCharacter } = useAuth();
  const nav = useNavigate();
  const [state, setState] = useState(null);
  const [actions, setActions] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [slots, setSlots] = useState([]);
  const [openVenue, setOpenVenue] = useState(null);
  const [nearVenue, setNearVenue] = useState(null);
  const [busy, setBusy] = useState(null);
  const [ad, setAd] = useState(null);
  const [showModes, setShowModes] = useState(false);
  const [toll, setToll] = useState(null);
  const [driving, setDriving] = useState(false);
  const [dir, setDir] = useState("down");
  const [moving, setMoving] = useState(false);
  const [travelTo, setTravelTo] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [traveling, setTraveling] = useState(false);
  const [burst, setBurst] = useState(0);
  const [pop, setPop] = useState({ k: 0, text: "", color: "#16A34A" });
  const celebrate = (text, color = "#16A34A") => { setBurst((b) => b + 1); sfx.coin(); if (text) setPop((p) => ({ k: p.k + 1, text, color })); };
  const [soundOn, setSoundOn] = useState(!sfx.isMuted());
  const audioStarted = useRef(false);
  const kickAudio = () => { sfx.resume(); if (!audioStarted.current) { audioStarted.current = true; sfx.ambientOn(); } };
  const toggleSound = () => { const on = !soundOn; setSoundOn(on); sfx.setMuted(!on); if (on) { sfx.resume(); sfx.ambientOn(); } };
  const [tod, setTod] = useState(dayTint());
  useEffect(() => { const t = setInterval(() => setTod(dayTint()), 60000); return () => clearInterval(t); }, []);

  const vpRef = useRef(null);
  const worldRef = useRef(null);
  const playerRef = useRef(null);
  const posRef = useRef({ x: WORLD_W * 0.5, y: WORLD_H * 0.42 });
  const velRef = useRef({ x: 0, y: 0 });
  const targetRef = useRef(null);
  const drivingRef = useRef(false);
  const dirRef = useRef("down");
  const movingRef = useRef(false);
  const nearRef = useRef(null);
  const tollTimerRef = useRef(0);

  const ownsCar = (character?.inventory || []).some((i) => i.kind === "car");

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([api.get("/game/state"), api.get("/actions")]);
      setState(s.data);
      setCharacter(s.data.character);
      setActions(a.data.actions);
    } catch (e) { toast.error(errMsg(e)); }
    try {
      const [inv, act] = await Promise.all([api.get("/ad-inventory"), api.get("/ads/active")]);
      const bySlot = {};
      act.data.ads.forEach((a) => { bySlot[a.slot_id] = a; });
      setSlots(inv.data.inventory
        .filter((s) => s.placement === "city" && s.status !== "unavailable" && typeof s.x === "number")
        .map((s) => ({ ...s, ad: bySlot[s.id] || null })));
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("ads skipped", e); }
    try {
      const g = await api.get("/gems/center");
      if (g.data.time_bonus.ready || g.data.quiz.ready) {
        toast("🎁 Free gems dey ready!", {
          description: "Claim your time bonus or ace the Naija quiz — no payment, ever.",
          action: { label: "Open", onClick: () => nav("/store") },
        });
      }
    } catch (e) { if (process.env.NODE_ENV === "development") console.debug("gems reminder skipped", e); }
    try { const v = await api.get("/travel/options"); setVehicles(v.data.vehicles); } catch (e) { if (process.env.NODE_ENV === "development") console.debug("vehicles skipped", e); }
  }, [setCharacter, nav]);

  useEffect(() => { load(); }, [load]);

  const c = state?.character || character;

  // main movement + camera loop
  useEffect(() => {
    let raf, last = performance.now(), tollAcc = 0;
    const venuesPx = VENUES.map((v) => ({ ...v, px: (v.x / 100) * WORLD_W, py: (v.y / 100) * WORLD_H }));
    const loop = (ts) => {
      const dt = Math.min(0.05, (ts - last) / 1000); last = ts;
      const spd = drivingRef.current ? DRIVE_SPEED : WALK_SPEED;
      let vx = velRef.current.x, vy = velRef.current.y;
      let active = Math.hypot(vx, vy) > 0.08;
      if (active) targetRef.current = null;
      if (!active && targetRef.current) {
        const dx = targetRef.current.x - posRef.current.x, dy = targetRef.current.y - posRef.current.y;
        const d = Math.hypot(dx, dy);
        if (d < 6) { targetRef.current = null; }
        else { vx = dx / d; vy = dy / d; active = true; }
      }
      if (active) {
        posRef.current.x = Math.max(24, Math.min(WORLD_W - 24, posRef.current.x + vx * spd * dt));
        posRef.current.y = Math.max(24, Math.min(WORLD_H - 24, posRef.current.y + vy * spd * dt));
        const nd = Math.abs(vx) > Math.abs(vy) ? (vx > 0 ? "right" : "left") : (vy > 0 ? "down" : "up");
        if (nd !== dirRef.current) { dirRef.current = nd; setDir(nd); }
      }
      if (active !== movingRef.current) { movingRef.current = active; setMoving(active); }

      // write transforms
      if (playerRef.current) playerRef.current.style.transform = `translate3d(${posRef.current.x}px,${posRef.current.y}px,0)`;
      const vp = vpRef.current;
      if (vp && worldRef.current) {
        const vw = vp.clientWidth, vh = vp.clientHeight;
        const camX = Math.max(0, Math.min(WORLD_W - vw, posRef.current.x - vw / 2));
        const camY = Math.max(0, Math.min(WORLD_H - vh, posRef.current.y - vh / 2));
        worldRef.current.style.transform = `translate3d(${-camX}px,${-camY}px,0)`;
      }
      // nearest venue
      let best = null, bd = ENTER_RADIUS;
      for (const v of venuesPx) {
        const d = Math.hypot(v.px - posRef.current.x, v.py - posRef.current.y);
        if (d < bd) { bd = d; best = v; }
      }
      const bestId = best?.id || null;
      if (bestId !== nearRef.current) { nearRef.current = bestId; setNearVenue(best || null); }

      // area-boys encounter check while moving
      if (active) {
        tollAcc += dt;
        if (tollAcc > 7 && Date.now() - tollTimerRef.current > 9000) {
          tollAcc = 0; tollTimerRef.current = Date.now();
          api.post("/city/toll/check").then((r) => {
            if (r.data.encounter) { setToll(r.data.encounter); sfx.error(); }
          }).catch(() => {});
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, []);

  // joystick
  const joyRef = useRef(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });
  const joyActive = useRef(false);
  const joyGeom = useRef({ cx: 0, cy: 0, r: 46 });
  const startJoy = (e) => {
    kickAudio();
    const rect = joyRef.current.getBoundingClientRect();
    joyGeom.current = { cx: rect.left + rect.width / 2, cy: rect.top + rect.height / 2, r: rect.width / 2 };
    joyActive.current = true;
    moveJoy(e);
  };
  const moveJoy = (e) => {
    if (!joyActive.current) return;
    const p = e.touches ? e.touches[0] : e;
    let dx = p.clientX - joyGeom.current.cx, dy = p.clientY - joyGeom.current.cy;
    const d = Math.hypot(dx, dy), max = joyGeom.current.r;
    if (d > max) { dx = (dx / d) * max; dy = (dy / d) * max; }
    setKnob({ x: dx, y: dy });
    velRef.current = { x: dx / max, y: dy / max };
  };
  const endJoy = () => { joyActive.current = false; setKnob({ x: 0, y: 0 }); velRef.current = { x: 0, y: 0 }; };

  const mapTap = (e) => {
    if (joyActive.current) return;
    kickAudio();
    const vp = vpRef.current.getBoundingClientRect();
    const vw = vpRef.current.clientWidth, vh = vpRef.current.clientHeight;
    const camX = Math.max(0, Math.min(WORLD_W - vw, posRef.current.x - vw / 2));
    const camY = Math.max(0, Math.min(WORLD_H - vh, posRef.current.y - vh / 2));
    const wx = e.clientX - vp.left + camX, wy = e.clientY - vp.top + camY;
    targetRef.current = { x: Math.max(24, Math.min(WORLD_W - 24, wx)), y: Math.max(24, Math.min(WORLD_H - 24, wy)) };
  };

  const enterVenue = (v) => {
    if (!v) return;
    sfx.tap();
    if (v.nav) { nav(v.nav); return; }
    if (v.id === "hustle") loadJobs();
    setOpenVenue(v.id);
  };
  const goVenue = (v) => { targetRef.current = { x: (v.x / 100) * WORLD_W, y: (v.y / 100) * WORLD_H }; };

  const takeRide = async (vehicle) => {
    sfx.whoosh();
    try {
      const { data } = await api.post("/travel", { vehicle_id: vehicle.id });
      setCharacter(data.character); setState((s) => ({ ...s, character: data.character }));
      const dest = travelTo; setTravelTo(null); setTraveling(true);
      setTimeout(() => {
        setTraveling(false);
        if (dest?.nav) nav(dest.nav);
        else if (dest) { enterVenue(dest); }
      }, Math.max(900, (data.secs || 1) * 900));
    } catch (e) { toast.error(errMsg(e)); }
  };

  const loadJobs = async () => {
    try { const { data } = await api.get("/jobs"); setJobs(data.jobs); } catch (e) { toast.error(errMsg(e)); }
  };
  const clickAd = async (e, adv) => {
    e.stopPropagation();
    try { const { data } = await api.post("/ads/click", { target_id: adv.id }); window.open(data.link, "_blank", "noopener"); }
    catch (err) { if (process.env.NODE_ENV === "development") console.debug("ad click failed", err); }
  };
  const doAction = async (id, title) => {
    setBusy(id);
    try {
      const { data } = await api.post("/actions/do", { action_id: id });
      setCharacter(data.character); setState((s) => ({ ...s, character: data.character }));
      toast.success(title + " ✓");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const work = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post("/jobs/work", { job_id: id });
      setCharacter(data.character); setState((s) => ({ ...s, character: data.character }));
      toast.success(`Shift done! +${naira(data.earned)}`); loadJobs();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const claimDaily = async () => {
    setBusy("daily");
    try {
      const { data } = await api.post("/daily/claim");
      setCharacter(data.character); setState((s) => ({ ...s, character: data.character }));
      celebrate(`+${naira(data.reward)}`, "#16A34A");
      toast.success(`Daily: ${naira(data.reward)}${data.gems ? ` +${data.gems}💎` : ""} · Streak ${data.streak}🔥`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const rollChaos = async () => {
    setBusy("chaos");
    try {
      const { data } = await api.post("/chaos/roll");
      setCharacter(data.character); setState((s) => ({ ...s, character: data.character }));
      const ev = data.event;
      const msg = `${ev.msg}${ev.wallet_delta ? ` (${ev.wallet_delta > 0 ? "+" : ""}${naira(ev.wallet_delta)})` : ""}`;
      if (ev.type === "good") { celebrate(ev.wallet_delta ? `+${naira(ev.wallet_delta)}` : "", "#16A34A"); toast.success(ev.title, { description: msg }); }
      else toast.error(ev.title, { description: msg });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  const resolveToll = async (action) => {
    try {
      const { data } = await api.post("/city/toll/resolve", { action });
      setCharacter(data.character); setState((s) => ({ ...s, character: data.character }));
      const o = data.outcome;
      o.delta < 0 ? toast.error("Area Boys", { description: o.msg }) : toast.success("Area Boys", { description: o.msg });
    } catch (e) { toast.error(errMsg(e)); } finally { setToll(null); }
  };
  const startAd = () => { setOpenVenue(null); setAd({ phase: "playing", count: 4 }); };
  useEffect(() => {
    if (!ad || ad.phase !== "playing") return;
    if (ad.count <= 0) {
      (async () => {
        try {
          const { data } = await api.post("/rewards/ad");
          setCharacter(data.character); setState((s) => ({ ...s, character: data.character }));
          celebrate(`+${naira(data.reward.cash)}`, "#FFD700");
          setAd({ phase: "reward", reward: data.reward, left: data.ads_left });
        } catch (e) { toast.error(errMsg(e)); setAd(null); }
      })();
      return;
    }
    const t = setTimeout(() => setAd((a) => ({ ...a, count: a.count - 1 })), 1000);
    return () => clearTimeout(t);
  }, [ad, setCharacter]);

  const toggleDrive = () => {
    if (!ownsCar) { toast.error("You need a car first", { description: "Buy a whip from the Shop 🚗" }); return; }
    kickAudio();
    const d = !driving; setDriving(d); drivingRef.current = d;
    if (d) { sfx.rev(); sfx.engineStart(); } else { sfx.engineStop(); }
    toast.success(d ? "Inside the whip 🚗 — vroom!" : "Back on foot 🚶🏾");
  };

  if (!c) return null;

  return (
    <div className="relative h-[100dvh] overflow-hidden bg-[#F8FAFC]" data-testid="city-page">
      {/* Top HUD */}
      <div className="absolute top-0 left-0 right-0 z-30 px-3 pt-3 pointer-events-none">
        <div className="flex items-center gap-2 bg-white/85 backdrop-blur-xl backdrop-saturate-150 border border-white/60 shadow-sm rounded-2xl px-3 py-2 pointer-events-auto">
          <button onClick={() => nav("/me")} data-testid="hud-avatar"
            className="w-[38px] h-[38px] rounded-full overflow-hidden bg-[#16A34A]/10 flex items-end justify-center shrink-0" style={{ boxShadow: "0 0 0 2px #16A34A" }}>
            <NaijaAvatar appearance={c.appearance} view="head" size={40} />
          </button>
          <div className="flex-1 min-w-0">
            <div className="font-display text-sm leading-none truncate">{c.name}</div>
            <div className="label-xs text-slate-500 mt-0.5">Lv{c.level} · {c.district_name} · {tod.icon} {tod.label}</div>
          </div>
          <div className="text-right leading-tight">
            <div className="text-[#16A34A] font-display text-xs" data-testid="hud-wallet">{naira(c.wallet)}</div>
            <div className="flex items-center gap-2 justify-end mt-0.5">
              <span className="text-[#FF0055] text-[0.7rem] font-semibold">{c.clout}★</span>
              <span className="text-[#FFD700] text-[0.7rem] font-semibold flex items-center gap-0.5"><Gem size={9} />{c.gems}</span>
            </div>
          </div>
        </div>
        <div className="mt-2 flex gap-1 pointer-events-auto" data-testid="needs-strip">
          {Object.keys(NEED_META).map((k) => {
            const v = Math.round(c.needs[k]);
            return (
              <div key={k} className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden" title={`${k} ${v}%`}>
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${v}%`, background: NEED_META[k], boxShadow: `0 0 6px ${NEED_META[k]}` }} />
              </div>
            );
          })}
        </div>
      </div>

      {/* Camera viewport */}
      <div ref={vpRef} onClick={mapTap} className="absolute inset-0 overflow-hidden cursor-pointer" data-testid="city-viewport">
        <div ref={worldRef} className="absolute top-0 left-0 will-change-transform"
          style={{ width: WORLD_W, height: WORLD_H, backgroundImage: `url(${MAP})`, backgroundSize: "cover", imageRendering: "auto" }}>
          {/* venues */}
          {VENUES.map((v) => (
            <button key={v.id} data-testid={`venue-${v.id}`}
              onClick={(e) => { e.stopPropagation(); if (v.nav) setTravelTo(v); else goVenue(v); }}
              className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center group z-10"
              style={{ left: `${v.x}%`, top: `${v.y}%` }}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl border backdrop-blur-sm transition-transform group-active:scale-90"
                style={{ background: `${v.color}22`, borderColor: `${v.color}aa`, boxShadow: tod.night ? `0 0 20px rgba(255,214,130,0.9), 0 0 10px ${v.color}66` : `0 0 14px ${v.color}55` }}>{v.emoji}</div>
              <span className="mt-1 label-xs px-1.5 py-0.5 rounded bg-white/90 shadow-sm whitespace-nowrap" style={{ color: v.color }}>{v.name}</span>
            </button>
          ))}
          {/* billboards — real inventory slots; empty ones invite advertisers */}
          {slots.map((s) => (
            s.ad ? (
              <button key={s.id} data-testid={`billboard-${s.id}`} onClick={(e) => clickAd(e, s.ad)}
                className="absolute -translate-x-1/2 -translate-y-1/2 z-[11] w-24 rounded-md overflow-hidden border-2 border-white shadow-lg bg-white active:scale-95"
                style={{ left: `${s.x}%`, top: `${s.y}%` }}>
                <img src={s.ad.image_url} alt={s.ad.advertiser_name} className="w-full h-12 object-cover" />
                <div className="text-[0.55rem] text-slate-700 bg-white py-0.5 text-center truncate px-1 font-semibold">{s.ad.advertiser_name}</div>
              </button>
            ) : (
              <button key={s.id} data-testid={`adslot-${s.id}`} onClick={(e) => { e.stopPropagation(); nav("/advertise"); }}
                className="absolute -translate-x-1/2 -translate-y-1/2 z-[11]" style={{ left: `${s.x}%`, top: `${s.y}%` }}>
                <motion.div animate={{ boxShadow: ["0 0 0px rgba(22,163,74,0.0)", "0 0 16px rgba(22,163,74,0.6)", "0 0 0px rgba(22,163,74,0.0)"] }}
                  transition={{ duration: 1.8, repeat: Infinity }}
                  className="w-24 h-14 rounded-md border-2 border-dashed border-[#16A34A] bg-white/95 flex flex-col items-center justify-center text-center">
                  <span className="text-[0.6rem] font-extrabold text-[#16A34A] leading-none">YOUR AD HERE</span>
                  <span className="text-[0.5rem] text-slate-500 mt-0.5">{s.neighborhood} · from {naira(s.base_price)}/7d</span>
                </motion.div>
              </button>
            )
          ))}
          {/* ambient crowd — makes the streets feel alive */}
          {NPCS.map((n) => (
            <motion.div key={n.id} className="absolute -translate-x-1/2 -translate-y-full z-[12] pointer-events-none"
              style={{ left: `${n.x}%`, top: `${n.y}%` }}
              animate={{ x: [0, n.rng, 0, -n.rng, 0] }}
              transition={{ duration: n.dur, repeat: Infinity, ease: "easeInOut" }}>
              <NaijaAvatar appearance={n.look} view="full" size={30} walking />
            </motion.div>
          ))}
          {/* player */}
          <div ref={playerRef} className="absolute top-0 left-0 z-20 will-change-transform" style={{ transform: `translate3d(${posRef.current.x}px,${posRef.current.y}px,0)` }}>
            <div className="-translate-x-1/2 -translate-y-full" data-testid="player-token">
              <RoamChar appearance={c.appearance} dir={dir} moving={moving} driving={driving} />
            </div>
          </div>
        </div>
        <motion.div animate={{ opacity: tod.op }} transition={{ duration: 1.5 }} className="absolute inset-0 pointer-events-none z-[14]" style={{ background: tod.bg }} data-testid="daynight-overlay" />
      </div>

      {/* Enter prompt */}
      <AnimatePresence>
        {nearVenue && !openVenue && !ad && !toll && (
          <motion.button initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
            data-testid="enter-venue-btn" onClick={() => enterVenue(nearVenue)}
            className="absolute left-1/2 -translate-x-1/2 bottom-28 z-30 bg-[#16A34A] text-white font-bold px-6 py-3 rounded-full shadow-[0_0_24px_rgba(0,255,102,0.5)] flex items-center gap-2">
            <Navigation size={16} /> Enter {nearVenue.name}
          </motion.button>
        )}
      </AnimatePresence>

      {/* Joystick */}
      <div ref={joyRef} data-testid="joystick"
        onTouchStart={startJoy} onTouchMove={moveJoy} onTouchEnd={endJoy}
        onMouseDown={startJoy} onMouseMove={moveJoy} onMouseUp={endJoy} onMouseLeave={endJoy}
        className="absolute left-5 bottom-24 z-30 w-24 h-24 rounded-full bg-white/70 backdrop-blur-md border border-white/60 shadow-md touch-none select-none">
        <div className="absolute left-1/2 top-1/2 w-11 h-11 -ml-[22px] -mt-[22px] rounded-full bg-[#16A34A]/80 border-2 border-white/40 shadow-lg pointer-events-none"
          style={{ transform: `translate(${knob.x}px,${knob.y}px)` }} />
      </div>

      {/* Right controls: Sound + Horn + Drive + Modes */}
      <div className="absolute right-4 bottom-24 z-30 flex flex-col gap-3">
        <button data-testid="sound-toggle" onClick={toggleSound}
          className={`w-14 h-14 rounded-full flex flex-col items-center justify-center border-2 border-white/60 backdrop-blur-sm shadow-md ${soundOn ? "bg-[#16A34A] text-white" : "bg-white/85 text-slate-500"}`}>
          {soundOn ? <Volume2 size={20} /> : <VolumeX size={20} />}
          <span className="label-xs mt-0.5">{soundOn ? "Sound" : "Muted"}</span>
        </button>
        {driving && (
          <button data-testid="horn-btn" onClick={() => sfx.horn()}
            className="w-14 h-14 rounded-full flex flex-col items-center justify-center border-2 border-white/60 bg-[#FFD700] text-slate-900 backdrop-blur-sm shadow-md active:scale-90 transition-transform">
            <Megaphone size={20} /><span className="label-xs mt-0.5">Horn</span>
          </button>
        )}
        <button data-testid="drive-toggle" onClick={toggleDrive}
          className={`w-14 h-14 rounded-full flex flex-col items-center justify-center border-2 backdrop-blur-sm shadow-md ${driving ? "bg-[#FF0055] border-white/60 text-white" : "bg-white/85 border-white/60 text-slate-700"}`}>
          {driving ? <Footprints size={20} /> : <Car size={20} />}
          <span className="label-xs mt-0.5">{driving ? "Walk" : "Drive"}</span>
        </button>
        <button data-testid="modes-btn" onClick={() => setShowModes(true)}
          className="w-14 h-14 rounded-full flex flex-col items-center justify-center border-2 border-white/60 bg-white/85 backdrop-blur-sm shadow-md text-[#D97706]">
          <LayoutGrid size={20} /><span className="label-xs mt-0.5">Modes</span>
        </button>
      </div>

      {/* Modes sheet */}
      <AnimatePresence>
        {showModes && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-40 bg-black/60" onClick={() => setShowModes(false)} />
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 260, damping: 28 }}
              className="absolute bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 rounded-t-3xl p-5 pb-8" data-testid="modes-sheet">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl">Game Modes</h2>
                <button data-testid="close-modes" onClick={() => setShowModes(false)} className="p-2 rounded-full bg-slate-100"><X size={18} /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {MODES.map((m) => (
                  <button key={m.label} data-testid={`mode-${m.label.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => { setShowModes(false); if (m.nav) nav(m.nav); }}
                    className="bg-slate-100 border border-slate-200 rounded-xl p-3 text-left hover:-translate-y-0.5 transition-transform" style={{ borderColor: `${m.color}44` }}>
                    <div className="text-2xl">{m.emoji}</div>
                    <div className="mt-1 text-sm font-semibold" style={{ color: m.color }}>{m.label}</div>
                    <div className="label-xs text-slate-500">{m.desc}</div>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Venue sheet */}
      <AnimatePresence>
        {openVenue && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-40 bg-black/60" onClick={() => setOpenVenue(null)} />
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 260, damping: 28 }}
              className="absolute bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 rounded-t-3xl p-5 pb-8 max-h-[70dvh] overflow-y-auto no-scrollbar" data-testid="venue-sheet">
              <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl">{VENUES.find((v) => v.id === openVenue)?.emoji} {VENUES.find((v) => v.id === openVenue)?.name}</h2>
                <button data-testid="close-sheet" onClick={() => setOpenVenue(null)} className="p-2 rounded-full bg-slate-100"><X size={18} /></button>
              </div>
              <VenueBody venue={openVenue} actions={actions} jobs={jobs} state={state} busy={busy}
                doAction={doAction} work={work} claimDaily={claimDaily} rollChaos={rollChaos} startAd={startAd} nav={nav} />
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Transport picker */}
      <AnimatePresence>
        {travelTo && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-40 bg-black/50" onClick={() => setTravelTo(null)} />
            <motion.div initial={{ y: "100%" }} animate={{ y: 0 }} exit={{ y: "100%" }} transition={{ type: "spring", stiffness: 260, damping: 28 }}
              className="absolute bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 rounded-t-3xl p-5 pb-8" data-testid="travel-sheet">
              <div className="flex items-center justify-between mb-1">
                <h2 className="font-display text-2xl">Travel to {travelTo.name} {travelTo.emoji}</h2>
                <button data-testid="close-travel" onClick={() => setTravelTo(null)} className="p-2 rounded-full bg-slate-100"><X size={18} /></button>
              </div>
              <p className="label-xs text-slate-500 mb-3">How you wan move? {travelTo.neighbourhood || "Across town"}</p>
              <div className="space-y-2">
                {vehicles.map((v) => (
                  <button key={v.id} data-testid={`ride-${v.id}`} onClick={() => takeRide(v)}
                    className="w-full flex items-center gap-3 bg-slate-50 hover:bg-[#16A34A]/10 border border-slate-200 rounded-xl px-3 py-2.5 text-left transition-colors">
                    <span className="text-2xl">{v.emoji}</span>
                    <div className="flex-1 min-w-0"><div className="text-sm font-semibold">{v.name}</div><div className="label-xs text-slate-500 truncate">{v.desc}</div></div>
                    <span className={`label-xs font-bold ${v.fare === 0 ? "text-[#16A34A]" : "text-slate-700"}`}>{v.fare === 0 ? "Free" : naira(v.fare)}</span>
                  </button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Travel animation */}
      <AnimatePresence>
        {traveling && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-[58] bg-[#0b1220] flex flex-col items-center justify-center" data-testid="travel-overlay">
            <motion.div animate={{ x: [-60, 60, -60] }} transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }} className="text-6xl">🚕</motion.div>
            <div className="mt-4 font-display text-xl text-white">On the way…</div>
            <div className="mt-2 w-40 h-1.5 rounded-full bg-white/20 overflow-hidden">
              <motion.div initial={{ width: 0 }} animate={{ width: "100%" }} transition={{ duration: 1.4 }} className="h-full bg-[#16A34A]" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Area-boys encounter */}
      <AnimatePresence>
        {toll && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-[55] bg-black/80 flex items-center justify-center px-6" data-testid="toll-overlay">
            <motion.div initial={{ scale: 0.8 }} animate={{ scale: 1 }} className="w-full max-w-xs bg-[#140608] border border-[#FF0055]/50 rounded-2xl p-5 text-center">
              <div className="text-5xl">🧍🏾‍♂️🧍🏾</div>
              <h3 className="font-display text-xl mt-3 text-[#FF0055]">Area Boys!</h3>
              <p className="text-sm text-slate-600 mt-2">{toll.text}</p>
              <p className="text-[#FFD700] font-display mt-2">Dem want {naira(toll.amount)}</p>
              <div className="mt-4 grid grid-cols-3 gap-2">
                <button data-testid="toll-pay" onClick={() => resolveToll("pay")} className="bg-[#16A34A] text-white font-bold py-2.5 rounded-lg label-xs">Pay</button>
                <button data-testid="toll-beg" onClick={() => resolveToll("beg")} className="bg-[#00E5FF]/20 text-[#00E5FF] font-bold py-2.5 rounded-lg label-xs">Beg</button>
                <button data-testid="toll-run" onClick={() => resolveToll("run")} className="bg-[#FF0055]/20 text-[#FF0055] font-bold py-2.5 rounded-lg label-xs">Run</button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rewarded bonus overlay */}
      <AnimatePresence>
        {ad && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-[60] bg-black flex flex-col items-center justify-center px-6" data-testid="ad-overlay">
            {ad.phase === "playing" ? (
              <>
                <div className="absolute top-4 right-4 label-xs text-slate-500">Sponsored · demo</div>
                <div className="w-full max-w-xs aspect-square rounded-2xl bg-gradient-to-br from-[#FF0055] via-[#A78BFA] to-[#00E5FF] flex flex-col items-center justify-center text-center p-6">
                  <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ duration: 1, repeat: Infinity }} className="text-6xl">📺</motion.div>
                  <div className="font-display text-2xl text-white mt-4">Your Ad Here</div>
                  <div className="text-white/80 text-sm mt-1">Detty December dey load…</div>
                </div>
                <div className="mt-6 label-xs text-slate-500">Reward in {ad.count}s…</div>
              </>
            ) : (
              <motion.div initial={{ scale: 0.7, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="flex flex-col items-center text-center" data-testid="ad-reward">
                <Sparkles size={48} className="text-[#FFD700] neon-text" />
                <h2 className="font-display text-3xl mt-4">Free Bonus! 🎁</h2>
                <div className="mt-3 text-[#16A34A] font-display text-2xl">+{naira(ad.reward.cash)}</div>
                {ad.reward.gems > 0 && <div className="mt-1 text-[#FFD700] font-display text-lg flex items-center gap-1"><Gem size={16} /> +{ad.reward.gems} gems</div>}
                <div className="label-xs text-slate-500 mt-3">{ad.left} free bonus left today</div>
                <button data-testid="ad-claim" onClick={() => setAd(null)} className="mt-7 bg-[#16A34A] text-white font-bold px-10 py-3 rounded-xl">Collect</button>
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <Confetti burst={burst} />
      <FloatUp fire={pop.k} text={pop.text} color={pop.color} />
    </div>
  );
}

function ActionBtn({ a, busy, onClick }) {
  const Icon = Icons[a.icon.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("")] || Icons.Zap;
  return (
    <button data-testid={`city-action-${a.id}`} disabled={busy === a.id} onClick={onClick}
      className="bg-slate-100 border border-slate-200 rounded-xl p-3 text-left hover:border-[#16A34A] transition-colors disabled:opacity-50">
      <div className="flex items-center justify-between">
        <Icon size={18} className="text-[#16A34A]" />
        <span className="text-xs text-slate-500">{a.cost ? naira(a.cost) : "Free"}</span>
      </div>
      <div className="mt-2 text-sm font-semibold">{a.title}</div>
      <div className="text-[0.7rem] text-slate-500 mt-0.5 leading-tight">{a.desc}</div>
    </button>
  );
}

function VenueBody(props) {
  const art = VENUE_ART[props.venue];
  return (
    <>
      {art && (
        <motion.img initial={{ opacity: 0, scale: 1.05 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
          src={art} alt="" className="w-full h-28 object-cover rounded-xl mb-3 shadow-sm" data-testid={`venue-art-${props.venue}`} />
      )}
      <VenueInner {...props} />
    </>
  );
}

function VenueInner({ venue, actions, jobs, state, busy, doAction, work, claimDaily, rollChaos, startAd, nav }) {
  const pick = (ids) => actions.filter((a) => ids.includes(a.id));
  const grid = (list) => (
    <div className="grid grid-cols-2 gap-2">
      {list.map((a) => <ActionBtn key={a.id} a={a} busy={busy} onClick={() => doAction(a.id, a.title)} />)}
    </div>
  );
  if (venue === "crib") {
    return (
      <>
        <p className="text-sm text-slate-500 mb-3">Rest, freshen up, use the toilet. Home sweet home — NEPA permitting. ⚡</p>
        {grid(pick(["sleep", "shower", "toilet"]))}
        <button data-testid="crib-furnish" onClick={() => nav("/furnish")} className="mt-3 w-full py-2.5 rounded-lg label-xs bg-[#16A34A]/15 text-[#15803D] flex items-center justify-center gap-2 font-bold">
          🛋️ Furnish your crib (Buy Mode)
        </button>
        <button data-testid="crib-rent" onClick={() => nav("/map")} className="mt-2 w-full py-2.5 rounded-lg label-xs bg-[#00E5FF]/15 text-[#00E5FF] flex items-center justify-center gap-2">
          <MapPin size={14} /> Rent & Districts {state?.rent?.overdue ? "· RENT OVERDUE ⚠️" : ""}
        </button>
      </>
    );
  }
  if (venue === "buka") return <>{<p className="text-sm text-slate-500 mb-3">Chop belle full. Mama put dey always deliver.</p>}{grid(pick(["buy_food", "suya"]))}</>;
  if (venue === "club") return <>{<p className="text-sm text-slate-500 mb-3">Spray money, catch vibes, boost your fun & social.</p>}{grid(pick(["club", "hangout"]))}</>;
  if (venue === "gym") return <>{<p className="text-sm text-slate-500 mb-3">Fitfam for the gram. Burn energy, gain clout cred.</p>}{grid(pick(["gym"]))}</>;
  if (venue === "hustle") {
    const ready = jobs.filter((j) => !j.locked && j.remaining === 0).slice(0, 4);
    return (
      <>
        <p className="text-sm text-slate-500 mb-3">Pick a shift and make money. Each gig burns energy + has cooldown.</p>
        <div className="space-y-2">
          {ready.length === 0 && <p className="text-sm text-slate-400">No shift ready. Rest or check the full board.</p>}
          {ready.map((j) => {
            const Icon = Icons[j.icon.split("-").map((s) => s[0].toUpperCase() + s.slice(1)).join("")] || Briefcase;
            return (
              <div key={j.id} className="bg-slate-100 border border-slate-200 rounded-xl p-3 flex items-center gap-3">
                <Icon size={18} className="text-[#16A34A] shrink-0" />
                <div className="flex-1 min-w-0"><div className="text-sm font-semibold truncate">{j.title}</div><div className="label-xs text-slate-500">{naira(j.pay)} · ⚡-{j.energy_cost}</div></div>
                <button data-testid={`city-work-${j.id}`} disabled={busy === j.id} onClick={() => work(j.id)} className="label-xs bg-[#16A34A] text-white px-4 py-2 rounded-lg disabled:opacity-50">Work</button>
              </div>
            );
          })}
        </div>
        <button onClick={() => nav("/jobs")} className="mt-3 w-full py-2.5 rounded-lg label-xs bg-slate-100 text-slate-600 flex items-center justify-center gap-2"><Briefcase size={14} /> Full job board</button>
      </>
    );
  }
  if (venue === "bank") {
    return (
      <>
        <p className="text-sm text-slate-500 mb-3">Collect your daily, tempt fate, invest, or top up gems.</p>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="city-daily" onClick={claimDaily} disabled={busy === "daily"} className="bg-[#FFD700] text-slate-900 font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"><Gift size={16} /> Daily</button>
          <button data-testid="city-chaos" onClick={rollChaos} disabled={busy === "chaos"} className="bg-slate-100 border border-[#00E5FF]/40 text-[#00E5FF] font-bold py-3 rounded-xl flex items-center justify-center gap-2 disabled:opacity-50"><Dice5 size={16} /> Street Roll</button>
        </div>
        <button data-testid="city-invest" onClick={() => nav("/invest")} className="mt-2 w-full py-3 rounded-xl label-xs bg-[#FF66B2]/15 text-[#FF66B2] flex items-center justify-center gap-2"><Icons.TrendingUp size={14} /> Invest — stocks & land 📈</button>
        <button data-testid="city-store" onClick={() => nav("/store")} className="mt-2 w-full py-3 rounded-xl label-xs bg-[#FFD700]/15 text-[#FFD700] flex items-center justify-center gap-2"><Gem size={14} /> Free Gems — earn, never pay 🎁</button>
        <button data-testid="city-freebonus" onClick={startAd} className="mt-2 w-full py-3 rounded-xl label-xs bg-[#16A34A]/15 text-[#16A34A] flex items-center justify-center gap-2"><PlayCircle size={14} /> Free Bonus — watch a short ad 🎁</button>
      </>
    );
  }
  return null;
}
